import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useTranslation } from '@shared/core';

export type CardSizeLabels = Readonly<{
  cocktail: string;
  luncheon: string;
  postcard4x6: string;
  magnet4x6: string;
}>;

// Untranslated fallback for callers outside a React/i18n context (and tests).
const DEFAULT_LABELS: CardSizeLabels = {
  cocktail: 'Cocktail',
  luncheon: 'Luncheon',
  postcard4x6: 'Postcard 4x6',
  magnet4x6: 'Magnet 4x6',
};

// Translated labels for named card sizes. The tq calls live here — directly in a
// hook with `useTranslation` in scope — so translation extraction can trace them;
// do not move them into `formatCardSizeName` (the extractor cannot follow
// function parameters).
export const useCardSizeLabels = (): CardSizeLabels => {
  const { tq } = useTranslation('stationery');
  return {
    cocktail: tq`Cocktail`,
    luncheon: tq`Luncheon`,
    postcard4x6: tq`Postcard 4x6`,
    magnet4x6: tq`Magnet 4x6`,
  };
};

// 4x6/6x4 are the physical postcard dimensions; the label is orientation-agnostic
// because a 6x4 landscape card is the same physical postcard rotated (orientation
// has its own picker).
export const isPostcardCardSize = (widthInches: number, heightInches: number): boolean => (widthInches === 4 && heightInches === 6) || (widthInches === 6 && heightInches === 4);

// Categories Joy sells as postcards. Both thank-you and save-the-date ship at
// postcard dimensions with a hand-addressed mailing back and no envelope; every
// other category at 4x6/6x4 would be a template-author mistake, so postcard-ness
// is category AND size. Mirrors card_service's POSTCARD_CATEGORIES / isPostcard.
const POSTCARD_CATEGORIES: ReadonlySet<StationeryTemplateCategoryEnum> = new Set([StationeryTemplateCategoryEnum.thankYou, StationeryTemplateCategoryEnum.saveTheDate]);

export const isPostcardCategory = (category: StationeryTemplateCategoryEnum | undefined): boolean => !!category && POSTCARD_CATEGORIES.has(category);

// A template is a postcard when it is a postcard category at postcard dimensions.
export const isPostcard = (category: StationeryTemplateCategoryEnum | undefined, widthInches: number, heightInches: number): boolean =>
  isPostcardCategory(category) && isPostcardCardSize(widthInches, heightInches);

// True when `formatCardSizeName` resolves to a named size ("Postcard 4x6",
// "Cocktail") rather than falling back to raw measurements — callers that
// compose their own label (product name + measurements) use this to avoid
// doubling up on names that already read as products.
export const hasNamedCardSize = (category: StationeryTemplateCategoryEnum | undefined, widthInches: number, heightInches: number, productType?: string): boolean =>
  formatCardSizeName(category, widthInches, heightInches, DEFAULT_LABELS, productType) !== `${widthInches} × ${heightInches} inches`;

/**
 * Customer-facing name for a card size. Named sizes (napkins, postcards) read as
 * products; everything else falls back to measurements. Pass labels from
 * `useCardSizeLabels()` where a React context is available.
 */
export const formatCardSizeName = (
  category: StationeryTemplateCategoryEnum | undefined,
  widthInches: number,
  heightInches: number,
  labels: CardSizeLabels = DEFAULT_LABELS,
  productType?: string,
): string => {
  // The magnet PRODUCT (MAGNET4X6) shares the saveTheDate postcard templates,
  // so the product — not category/size — decides its name.
  if (productType === 'magnet' && isPostcardCardSize(widthInches, heightInches)) {
    return labels.magnet4x6;
  }
  if (category === StationeryTemplateCategoryEnum.napkin) {
    if (widthInches === 4.75) return labels.cocktail;
    if (widthInches === 6.5) return labels.luncheon;
  }
  if (isPostcard(category, widthInches, heightInches)) {
    return labels.postcard4x6;
  }
  return `${widthInches} × ${heightInches} inches`;
};

/**
 * Row title for a concrete product at a card size, as the product+size pickers
 * show it: "Postcard 4x6", "Magnet 4x6", "Classic Paper 5x7", "Premium Digital".
 * Shared by the PDP picker and the customizer's product switcher so one product
 * never reads differently between the two screens.
 */
export const formatProductSizeTitle = (
  category: StationeryTemplateCategoryEnum | undefined,
  widthInches: number,
  heightInches: number,
  labels: CardSizeLabels,
  product: Readonly<{ productType: string; cardTypeSKU?: unknown; translations: Readonly<{ displayName: string }> }>,
): string => {
  // Digital products have no size axis; their product name is the label.
  if (!product.cardTypeSKU) return product.translations.displayName;
  // Named sizes already read as products ("Postcard 4x6", "Magnet 4x6",
  // "Cocktail") — prefixing the product name would double it up.
  if (hasNamedCardSize(category, widthInches, heightInches, product.productType)) {
    return formatCardSizeName(category, widthInches, heightInches, labels, product.productType);
  }
  // Compact "5x7", matching the named labels ("Postcard 4x6", "Magnet 4x6").
  return `${product.translations.displayName} ${widthInches}x${heightInches}`;
};
